'use client';

import { usePathname } from '@/i18n/routing';

/**
 * Typed route-kind flags derived from the current (locale-stripped) pathname.
 *
 * WHY THIS HOOK EXISTS (#5814 — follow-up to #5158/#5160).
 * `MainContentArea` historically declared this set of pathname checks inline
 * (`isSearchPage`, `isResearchPage`, `isStoragePage`, `isDocAnalysisPage`,
 * `isDocumentPage`, `isSharePage`, plus the derived `isAnonSidebarPage` and
 * `rendersOwnFullBleedShell`). As more pages get added these inline checks drift
 * across components. Centralizing them in one typed hook removes the duplication
 * and makes the derivation independently unit-testable.
 *
 * `usePathname` from `@/i18n/routing` returns the CANONICAL pathname WITHOUT the
 * locale prefix (e.g. `/document/...`, never `/sk/dokument/...`), so every flag
 * below compares against canonical, locale-stripped paths.
 */
export interface RouteKind {
  /**
   * The canonical, locale-stripped pathname this kind was derived from (as
   * returned by `usePathname` from `@/i18n/routing`). Exposed so consumers that
   * also need the raw pathname (e.g. for `getActivePanelKind` / sidebar-section
   * lookups) can read it from the single `useRouteKind()` call rather than
   * invoking `usePathname()` a second time (#5879).
   */
  pathname: string;
  /** Any session-share route (`/.../share/...`). */
  isSharePage: boolean;
  /**
   * A chat SESSION page (`/chat/:sessionId`) — i.e. `/chat/...` but NOT the
   * `/chat` landing and NOT a `/chat/share/...` route. This is distinct from
   * `isResearchPage` (the empty landings) — it's a real session view with a
   * conversation in it. Consumers gate session-only affordances on this (e.g.
   * the mobile "New Chat" button in `FloatingHeaderControls`, #5901).
   */
  isChatSessionPage: boolean;
  /**
   * A research SESSION page (`/research/:sessionId`) — i.e. `/research/...` but
   * NOT the `/research` landing and NOT a `/research/share/...` route. The
   * symmetric counterpart to `isChatSessionPage`: a real session view with a
   * conversation in it, distinct from `isResearchPage` (the empty landings).
   * Consumers gate session-only affordances on this alongside
   * `isChatSessionPage` (e.g. the mobile "New Chat" button in
   * `FloatingHeaderControls`, #5908).
   */
  isResearchSessionPage: boolean;
  /** Document viewer (`/document/:id`). Renders its own full-bleed grid. */
  isDocumentPage: boolean;
  /** Search results page (`/search`). */
  isSearchPage: boolean;
  /** Research/chat landing entry points (`/research`, `/chat`, `/`). */
  isResearchPage: boolean;
  /** Storage / file browser (`/storage`). */
  isStoragePage: boolean;
  /** Document-analysis page (`/document-analysis`). */
  isDocAnalysisPage: boolean;
  /** Document-generation page (`/generovanie` and `/generovanie/:sessionId`). */
  isDocGenPage: boolean;
  /**
   * Pages where anonymous users see a (simplified) sidebar — the union of
   * search, research, storage, document-analysis and the chats-list page.
   */
  isAnonSidebarPage: boolean;
  /**
   * Pages that render their own full-bleed shell (a width:100% grid / sidebar /
   * file browser) and must therefore never be horizontally centered by a parent
   * `<main>`. Centering a flex column shrinks the unconstrained children wrapper
   * to its intrinsic width, squashing the full-bleed content (#5158, #5160).
   */
  rendersOwnFullBleedShell: boolean;
}

/**
 * Pure pathname → route-kind derivation. Exported so the mapping can be unit
 * tested directly with string inputs (no React renderer, no `usePathname` mock).
 *
 * @param pathname canonical, locale-stripped pathname (as returned by
 *   `usePathname` from `@/i18n/routing`).
 */
export function deriveRouteKind(pathname: string): RouteKind {
  const isSharePage = pathname.includes('/share/');
  // A chat session is `/chat/<id>` — startsWith('/chat/') excludes the bare
  // `/chat` landing; the `!isSharePage` guard excludes `/chat/share/<id>`.
  const isChatSessionPage = pathname.startsWith('/chat/') && !isSharePage;
  // A research session is `/research/<id>` — startsWith('/research/') excludes
  // the bare `/research` landing; the `!isSharePage` guard excludes
  // `/research/share/<id>`.
  const isResearchSessionPage =
    pathname.startsWith('/research/') && !isSharePage;
  // next-intl returns the canonical pathname (/document), not localized (/dokument)
  const isDocumentPage = pathname.startsWith('/document/');
  const isSearchPage = pathname === '/search';
  const isResearchPage =
    pathname === '/research' || pathname === '/chat' || pathname === '/';
  const isStoragePage = pathname === '/storage';
  const isDocAnalysisPage = pathname === '/document-analysis';
  // The chats list page (`/chats`) — renders its own full-width list shell and
  // centers its content internally, so it must not be squashed by <main>'s
  // centering. The "Chats" sidebar nav item is anon-visible (not in
  // AUTH_ONLY_SECTIONS, sidebarSections.tsx), so this IS part of
  // isAnonSidebarPage — otherwise the mobile sidebar/hamburger unmounts the
  // instant an anon user follows that very link.
  const isChatsListPage = pathname === '/chats';
  // The document-generation page (`/generovanie` and `/generovanie/:sessionId`)
  // renders its own two-column sidebar layout and must never be centered.
  const isDocGenPage =
    pathname === '/generovanie' || pathname.startsWith('/generovanie/');
  const isAnonSidebarPage =
    isSearchPage ||
    isResearchPage ||
    isStoragePage ||
    isDocAnalysisPage ||
    isChatsListPage;
  const rendersOwnFullBleedShell =
    isDocumentPage || isAnonSidebarPage || isChatsListPage || isDocGenPage;

  return {
    pathname,
    isSharePage,
    isChatSessionPage,
    isResearchSessionPage,
    isDocumentPage,
    isSearchPage,
    isResearchPage,
    isStoragePage,
    isDocAnalysisPage,
    isDocGenPage,
    isAnonSidebarPage,
    rendersOwnFullBleedShell,
  };
}

/**
 * Returns the typed route-kind flags for the current pathname.
 *
 * Thin wrapper over `deriveRouteKind` + `usePathname`; the derivation lives in
 * the pure function so it can be tested without a renderer.
 */
export function useRouteKind(): RouteKind {
  const pathname = usePathname();
  return deriveRouteKind(pathname);
}
